<template>
  <div class="flex justify-between items-center grow-0">
    <VPButton
      :disabled="disabled || value === 0"
      rounded
      class="px-2 py-0.5"
      theme="base"
      :size="size"
      @click="onRemove"
    >
      <p
        class="text-base w-3"
        :class="fontClass"
      >
        -
      </p>
      <Accessible :description="$gettext('Remove 1')" />
    </VPButton>
    <input
      :id="inputId"
      class="w-8 text-base text-center outline-hidden"
      :class="[
        fontClass,
        {
          'text-gray-400': value === 0,
        },
      ]"
      :disabled="disabled"
      :value="value"
      @change="onManualEntry"
      @keydown.enter.prevent="onManualEntry"
    >
    <Accessible>
      <label :for="inputId">{{ $gettext('Input a number') }}</label>
    </Accessible>
    <VPButton
      :disabled="disabled"
      rounded
      class="px-2 py-0.5"
      theme="base"
      :size="size"
      @click="onAdd"
    >
      <p
        class="text-base w-3"
        :class="fontClass"
      >
        +
      </p>
      <Accessible :description="$gettext('Add 1')" />
    </VPButton>
  </div>
</template>

<script setup lang="ts">
import VPButton from './VPButton.vue';
import { Accessible } from '@vp/vue-components';
import { useGettext } from 'vue3-gettext';
import { computed, ref } from 'vue';
import seedrandom from 'seedrandom';

const { $gettext } = useGettext();

interface Props {
  value: number;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  fontClass?: string;
  multiplier?: number;
  minimum?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xs',
  multiplier: 1,
});

const emit = defineEmits<{
  (e: 'set', num: number): void;
  (e: 'add', delta: number): void;
}>();

const idGenerator = seedrandom();
const inputId = ref<string>(String(idGenerator.int32()));

// The step is the "sell in multiples of" value; minimum is a separate floor on
// the purchasable quantity. The first non-zero quantity is the smallest
// positive multiple of the step that is still >= minimum.
const step = computed(() => props.multiplier || 1);
const startingQuantity = computed(() => {
  const s = step.value;
  const min = props.minimum && props.minimum > 0 ? props.minimum : s;
  return Math.ceil(min / s) * s;
});

const onAdd = () => {
  emit('add', props.value === 0 ? startingQuantity.value : step.value);
};

const onRemove = () => {
  emit('add', props.value <= startingQuantity.value ? -props.value : -step.value);
};

const onManualEntry = (e: Event) => {
  const input = e.target as HTMLInputElement | null;
  if (!input) {
    return;
  };
  emit('set', Number(input.value));
};
</script>
